import { CopyOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { message, Popconfirm } from "antd";
import { AppButton, AppInput, AppSelect, AppTextArea } from "components/antd-ui";
import { memo, useCallback, type ChangeEvent, type ReactNode } from "react";
import { ScenarioNode, ScenarioNodeOption, ScenarioNodeType } from "types/scenario";
import { NODE_KEY_ERROR, NODE_LINK_ERROR, NODE_TYPE_OPTIONS } from "../constants";
import { getChoiceOptionBlockedKeys } from "../utils";
import "./NodeInspector.scss";

type OptionRowProps = {
  option: ScenarioNodeOption;
  optionIndex: number;
  nodeKeyOptions: { value: string; label: string }[];
  onChange: (index: number, label: string) => void;
  onNextChange: (index: number, nextNodeKey: string) => void;
  onDelete: (index: number) => void;
};

const OptionRow = memo(
  ({ option, optionIndex, nodeKeyOptions, onChange, onNextChange, onDelete }: OptionRowProps) => {
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange(optionIndex, event.target.value);
      },
      [onChange, optionIndex],
    );

    const handleNextChange = useCallback(
      (value: string | undefined) => {
        onNextChange(optionIndex, value ?? "");
      },
      [onNextChange, optionIndex],
    );

    const handleDelete = useCallback(() => {
      onDelete(optionIndex);
    }, [onDelete, optionIndex]);

    return (
      <div className="nodeInspectorOption">
        <AppInput value={option.label} onChange={handleChange} />
        <AppSelect
          className="nodeInspectorOptionSelect"
          value={option.nextNodeKey || undefined}
          options={nodeKeyOptions}
          placeholder="nextNodeKey"
          allowClear
          onChange={handleNextChange}
        />
        <button type="button" className="nodeInspectorDeleteOption" onClick={handleDelete} title="Xóa">
          ×
        </button>
      </div>
    );
  },
);

OptionRow.displayName = "OptionRow";

type NodeTypeFieldsProps = {
  node: ScenarioNode;
  nodes: ScenarioNode[];
  nodeKeyOptions: { value: string; label: string }[];
  onContentChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onOptionChange: (index: number, label: string) => void;
  onOptionNextChange: (index: number, nextNodeKey: string) => void;
  onOptionDelete: (index: number) => void;
  onAddOption: () => void;
};

const NodeTypeFields = memo(
  ({
    node,
    nodes,
    nodeKeyOptions,
    onContentChange,
    onOptionChange,
    onOptionNextChange,
    onOptionDelete,
    onAddOption,
  }: NodeTypeFieldsProps) => {
    let contentLabel = "Nội dung phản hồi";

    switch (node.type) {
      case ScenarioNodeType.Input:
        contentLabel = "Câu hỏi thu thập";
        break;
      case ScenarioNodeType.End:
        contentLabel = "Nội dung kết thúc";
        break;
      default:
        break;
    }

    const contentField = (
      <AppTextArea
        label={contentLabel}
        className="nodeInspectorTextarea"
        value={node.content}
        onChange={onContentChange}
      />
    );

    switch (node.type) {
      case ScenarioNodeType.Choice:
        return (
          <>
            {contentField}
            <p className="nodeInspectorHint">Bạn có thể dùng biến, ví dụ: {"{{name}}"}.</p>
            <hr className="nodeInspectorDivider" />
            <span className="nodeInspectorLabel">Lựa chọn · mỗi option một nextNodeKey</span>
            {node.options?.map((option, index) => {
              const blocked = getChoiceOptionBlockedKeys(nodes, node, index);

              return (
                <OptionRow
                  key={`${option.position}-${index}`}
                  option={option}
                  optionIndex={index}
                  nodeKeyOptions={nodeKeyOptions.filter((item) => !blocked.has(item.value))}
                  onChange={onOptionChange}
                  onNextChange={onOptionNextChange}
                  onDelete={onOptionDelete}
                />
              );
            })}
            <AppButton
              className="nodeInspectorAddOption"
              size="small"
              icon={<PlusOutlined />}
              onClick={onAddOption}
            >
              Thêm lựa chọn
            </AppButton>
            <p className="nodeInspectorHint">
              Mỗi lựa chọn đi một nhánh qua options[].nextNodeKey. Không chọn node phía trên Choice hoặc
              node đã thuộc nhánh khác.
            </p>
          </>
        );
      case ScenarioNodeType.End:
        return contentField;
      case ScenarioNodeType.Input:
      case ScenarioNodeType.Message:
        return (
          <>
            {contentField}
            <p className="nodeInspectorHint">Bạn có thể dùng biến, ví dụ: {"{{name}}"}.</p>
          </>
        );
      default:
        return null;
    }
  },
);

NodeTypeFields.displayName = "NodeTypeFields";

type NodeInspectorProps = {
  node: ScenarioNode | null;
  nodes: ScenarioNode[];
  nodeKeyOptions: { value: string; label: string }[];
  blockedNextKeys?: string[];
  saving?: boolean;
  onChange: (node: ScenarioNode) => void;
  onSave: (node: ScenarioNode) => void;
  onCancel: (node: ScenarioNode) => void;
  onDuplicate: () => void;
  onDelete: (node: ScenarioNode) => void;
};

export const NodeInspector = memo(
  ({ node, nodes, nodeKeyOptions, blockedNextKeys, saving, onChange, onSave, onCancel, onDuplicate, onDelete }: NodeInspectorProps) => {
    const handleKeyChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        if (!node) {
          return;
        }

        const value = event.target.value;

        if (nodes.some((item) => item.id !== node.id && item.key === value)) {
          message.warning(NODE_KEY_ERROR);
          return;
        }

        onChange({ ...node, key: value });
      },
      [node, nodes, onChange],
    );

    const handleTypeChange = useCallback(
      (value: ScenarioNodeType) => {
        if (!node) {
          return;
        }

        if (value === ScenarioNodeType.Choice) {
          onChange({
            ...node,
            type: value,
            nextNodeKey: "",
            options: node.options?.length
              ? node.options
              : [{ label: "Lựa chọn mới", nextNodeKey: node.nextNodeKey || "", position: 0 }],
          });
          return;
        }

        onChange({
          ...node,
          type: value,
          options: [],
          nextNodeKey:
            node.type === ScenarioNodeType.Choice
              ? node.options?.[0]?.nextNodeKey || node.nextNodeKey || ""
              : node.nextNodeKey,
        });
      },
      [node, onChange],
    );

    const handleContentChange = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement>) => {
        if (!node) {
          return;
        }

        onChange({ ...node, content: event.target.value });
      },
      [node, onChange],
    );

    const handleOptionChange = useCallback(
      (index: number, label: string) => {
        if (!node) {
          return;
        }

        onChange({
          ...node,
          options: node.options?.map((option, optionIndex) =>
            optionIndex === index ? { ...option, label } : option,
          ),
        });
      },
      [node, onChange],
    );

    const handleOptionNextChange = useCallback(
      (index: number, nextNodeKey: string) => {
        if (!node) {
          return;
        }

        if (nextNodeKey && blockedNextKeys?.includes(nextNodeKey)) {
          message.warning(NODE_LINK_ERROR.ancestor);
          return;
        }

        if (nextNodeKey && getChoiceOptionBlockedKeys(nodes, node, index).has(nextNodeKey)) {
          message.warning(NODE_LINK_ERROR.branch);
          return;
        }

        onChange({
          ...node,
          options: node.options?.map((option, optionIndex) =>
            optionIndex === index ? { ...option, nextNodeKey } : option,
          ),
        });
      },
      [blockedNextKeys, node, nodes, onChange],
    );

    const handleOptionDelete = useCallback(
      (index: number) => {
        if (!node) {
          return;
        }

        onChange({
          ...node,
          options: node.options?.filter((_, optionIndex) => optionIndex !== index),
        });
      },
      [node, onChange],
    );

    const handleAddOption = useCallback(() => {
      if (!node) {
        return;
      }

      const nextPosition = node.options?.length ?? 0;

      onChange({
        ...node,
        options: [...(node.options ?? []), { label: "Lựa chọn mới", nextNodeKey: "", position: nextPosition }],
      });
    }, [node, onChange]);

    const handleSave = useCallback(() => {
      if (!node) {
        return;
      }

      onSave(node);
    }, [node, onSave]);

    const handleCancel = useCallback(() => {
      if (!node) {
        return;
      }

      onCancel(node);
    }, [node, onCancel]);

    const handleDelete = useCallback(() => {
      if (!node) {
        return;
      }

      onDelete(node);
    }, [node, onDelete]);

    let body: ReactNode;

    if (!node) {
      body = <div className="nodeInspectorEmpty">Chọn một node để chỉnh sửa thuộc tính.</div>;
    } else {
      body = (
        <div className="nodeInspectorBody">
          <AppInput label="Tên định danh" value={node.key} onChange={handleKeyChange} />

          <span className="nodeInspectorLabel">Loại node</span>
          <AppSelect
            className="nodeInspectorSelect"
            value={node.type}
            options={NODE_TYPE_OPTIONS}
            searchable={false}
            onChange={handleTypeChange}
          />

          <NodeTypeFields
            node={node}
            nodes={nodes}
            nodeKeyOptions={nodeKeyOptions}
            onContentChange={handleContentChange}
            onOptionChange={handleOptionChange}
            onOptionNextChange={handleOptionNextChange}
            onOptionDelete={handleOptionDelete}
            onAddOption={handleAddOption}
          />

          <div className="nodeInspectorActions">
            <AppButton onClick={handleCancel}>Hủy</AppButton>
            <AppButton tone="primary" loading={saving} onClick={handleSave}>
              Lưu node
            </AppButton>
          </div>
        </div>
      );
    }

    return (
      <aside className="nodeInspector">
        <div className="scenarioPanelHead">
          <h2>Thuộc tính node</h2>
          {node ? (
            <div className="nodeInspectorHeadActions">
              <AppButton size="small" icon={<CopyOutlined />} onClick={onDuplicate} aria-label="Sao chép node" />
              <Popconfirm
                title="Xác nhận xóa?"
                okText="Xóa"
                cancelText="Không"
                onConfirm={handleDelete}
              >
                <AppButton size="small" icon={<DeleteOutlined />} aria-label="Xóa node" />
              </Popconfirm>
            </div>
          ) : null}
        </div>
        {body}
      </aside>
    );
  },
);

NodeInspector.displayName = "NodeInspector";
